import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { sprintService } from '@/services/sprintService'

// Sprint, story points and original estimate of a task, plus the estimate-vs-logged bar.
// `onUpdate(field, value)` saves one field (TaskPage.updateField).

export const formatSeconds = (seconds) => {
  if (!seconds) return '0m'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  return h ? `${h}h` : `${m}m`
}

const levelOf = (type) => type?.hierarchy_level ?? 0

export const SprintField = ({ task, projectId, onUpdate, labelClassName = 'text-xs text-muted-foreground font-medium', controlClassName = 'h-9' }) => {
  const { data } = useQuery(['projects', String(projectId), 'sprints'], () => sprintService.getSprints(projectId), { staleTime: 30000 })
  const sprints = data?.data?.sprints || []
  const level = levelOf(task.type)
  const reason = level > 0 ? "Epics aren't planned into sprints" : level < 0 ? "Sub-tasks follow their parent's sprint" : null
  // A closed sprint the task stayed in (done work) isn't in the open list; show it anyway
  const current = task.sprint && !sprints.some(s => s.id === task.sprint.id) ? task.sprint : null

  return (
    <div className="space-y-1.5">
      <label className={labelClassName}>Sprint</label>
      <Select
        value={task.sprint_id ? String(task.sprint_id) : 'backlog'}
        onValueChange={(v) => onUpdate('sprint_id', v === 'backlog' ? null : Number(v))}
        disabled={!!reason}
      >
        <SelectTrigger className={controlClassName} title={reason || undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="backlog">Backlog (no sprint)</SelectItem>
          {current && <SelectItem value={String(current.id)} disabled>{current.name} ({current.state})</SelectItem>}
          {sprints.map(s => (
            <SelectItem key={s.id} value={String(s.id)}>{s.name}{s.state === 'active' ? ' · active' : ''}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {reason && <p className="text-[11px] text-muted-foreground">{reason}</p>}
    </div>
  )
}

// Saves on Enter or when the field loses focus; Escape restores the saved value
const InlineValue = ({ value, placeholder, onSave, inputMode, ariaLabel, className }) => {
  const [draft, setDraft] = useState(value ?? '')
  useEffect(() => { setDraft(value ?? '') }, [value])
  const commit = () => {
    const next = String(draft).trim()
    if (next !== String(value ?? '')) onSave(next === '' ? null : next)
  }
  return (
    <Input
      value={draft}
      inputMode={inputMode}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={className}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') { setDraft(value ?? ''); e.currentTarget.blur() }
      }}
    />
  )
}

export const EstimateFields = ({ task, onUpdate, labelClassName = 'text-xs text-muted-foreground font-medium', controlClassName = 'h-9' }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="space-y-1.5">
      <label className={labelClassName}>Story points</label>
      <InlineValue
        value={task.story_points ?? ''}
        placeholder="—"
        inputMode="decimal"
        ariaLabel="Story points"
        className={controlClassName}
        onSave={(v) => onUpdate('story_points', v === null ? null : Number(v))}
      />
    </div>
    <div className="space-y-1.5">
      <label className={labelClassName}>Estimate</label>
      <InlineValue
        value={task.original_estimate_seconds ? formatSeconds(task.original_estimate_seconds) : ''}
        placeholder="e.g. 3h, 2d"
        ariaLabel="Original estimate"
        className={controlClassName}
        onSave={(v) => onUpdate('original_estimate', v)}
      />
    </div>
  </div>
)

// "Logged 1h 30m of 4h · 2h 30m left" with a bar; red when over the estimate
export const EstimateProgress = ({ estimate, logged }) => {
  if (!estimate) return null
  const over = logged > estimate
  const percent = Math.min(100, Math.round((logged / estimate) * 100))
  return (
    <div className="space-y-1">
      <Progress value={percent} className={cn('h-1.5', over && '[&>div]:bg-destructive')} />
      <p className={cn('text-xs text-muted-foreground', over && 'text-destructive')}>
        Logged {formatSeconds(logged)} of {formatSeconds(estimate)} · {over ? `${formatSeconds(logged - estimate)} over` : `${formatSeconds(estimate - logged)} left`}
      </p>
    </div>
  )
}
